/**
 * Size Chart — PDP size guide sheet + "checked the size chart?" gate.
 *
 * Reads the chart JSON embedded by blocks/product-size-chart.liquid (and the
 * per-piece regions in blocks/product-set-buy.liquid) and:
 *   1. Moves the "Size chart" link into the Size option label row of the
 *      product-variants block (right-aligned; stays inline as fallback).
 *   2. Renders the sheet body: measurement table (sizes as rows, IN/CM
 *      toggle, sticky header, selected-size highlight), inseam table,
 *      how-to-measure, fit notes.
 *   3. Add-to-cart gate (theme setting size_chart_warning_enabled): if the
 *      shopper hasn't opened the size chart for this product FAMILY this
 *      session, the first add-to-cart opens a small prompt. Covers the main
 *      PDP form, the floating-bar add-to-cart, and (via HealixSizeChart.gateSet,
 *      called from healix-set.js) the set PDP's "Add set to bag". Quick-view /
 *      variant-sheet quick-add flows are deliberately NOT gated. Runs AFTER the
 *      variant pending gate (product.js) — while size/length are unselected
 *      this script stays out of the way.
 *
 * TRACKING KEYS (sessionStorage — "this session" semantics by design):
 *   healix:size-chart:viewed:v2    { [familyKey]: ts }
 *     familyKey = the product FAMILY base handle (colourway suffix stripped
 *     server-side into data-family, e.g. every Elan pant colourway shares one
 *     key). Marked when the chart sheet for that family is opened from ANY
 *     trigger (PDP link, set per-piece link, the warning's own View button).
 *   healix:size-chart:dismissed:v2 { [productHandle]: ts }
 *     Set when the shopper picks "Add anyway" — that product (set: the set
 *     shell product) never nags again this session.
 *
 * INSTANT-NAV SAFETY: this file is re-executed after every PJAX swap
 * (assets/instant-nav.js re-runs scripts inside #main-content). All document-
 * level listeners are registered exactly ONCE (module singleton) and resolve
 * the ACTIVE size-chart root(s) from the live DOM at event time — never from
 * closures captured at init. Sheets teleported to <body> are tagged and any
 * stale ones (from a previous page, possibly with the SAME ids) are removed on
 * every boot before the new page's sheets are teleported.
 */
(function () {
  'use strict';

  // Re-execution guard: instant-nav re-runs this file after every swap; the
  // singleton keeps document-level listeners unique and just re-boots.
  if (window.__healixSizeChart) { window.__healixSizeChart.boot(); return; }

  var VIEWED_KEY = 'healix:size-chart:viewed:v2';
  var DISMISSED_KEY = 'healix:size-chart:dismissed:v2';

  function readStore(key) {
    try { return JSON.parse(sessionStorage.getItem(key) || '{}') || {}; }
    catch (e) { return {}; }
  }
  function writeStore(key, obj) {
    try { sessionStorage.setItem(key, JSON.stringify(obj)); } catch (e) { /* private mode */ }
  }
  function isViewed(familyKey) {
    return !!(familyKey && readStore(VIEWED_KEY)[familyKey]);
  }
  function markViewed(familyKey) {
    if (!familyKey) return;
    var viewed = readStore(VIEWED_KEY);
    if (viewed[familyKey]) return;
    viewed[familyKey] = Date.now();
    writeStore(VIEWED_KEY, viewed);
  }
  function isDismissed(handle) {
    return !!(handle && readStore(DISMISSED_KEY)[handle]);
  }
  function markDismissed(handle) {
    if (!handle) return;
    var dismissed = readStore(DISMISSED_KEY);
    dismissed[handle] = Date.now();
    writeStore(DISMISSED_KEY, dismissed);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c];
    });
  }
  function toCm(v) {
    var n = Number(v);
    if (!isFinite(n)) return null;
    var cm = Math.round(n * 2.54 * 10) / 10;
    return cm % 1 === 0 ? String(cm.toFixed(0)) : String(cm);
  }
  function fmt(v, unit) {
    if (v == null || v === '') return null;
    if (unit === 'cm') return toCm(v);
    var n = Number(v);
    return isFinite(n) ? String(n) : String(v);
  }

  /* ================= Sheet teleport + stale-sheet cleanup ================= */

  // position:fixed sheets get trapped by transformed/filtered ancestors inside
  // the PDP section, so we teleport them to <body> (tagged for cleanup).
  function teleport(id) {
    if (!id) return;
    var s = document.querySelector('[data-ui-sheet="' + id + '"]');
    var o = document.querySelector('[data-ui-sheet-overlay="' + id + '"]');
    [o, s].forEach(function (el) {
      if (!el) return;
      el.setAttribute('data-sc-teleported', '');
      if (el.parentElement !== document.body) document.body.appendChild(el);
    });
  }

  function cssEsc(s) { return String(s).replace(/["\\]/g, '\\$&'); }

  // After a PJAX swap the previous page's teleported sheets are still in
  // <body>, and PDPs rendered from the same template share BLOCK ids — so the
  // new page's sheet ids COLLIDE with the stale nodes and every id-based
  // lookup (UISheet.open, our render target) would hit the stale one: charts
  // showing the previous product, warn buttons firing twice, the reported
  // flakiness. Rule: when a fresh (not-yet-teleported) duplicate of an id has
  // arrived with the new page, the stale teleported copy is removed and the
  // fresh one wins. Teleported sheets with NO fresh duplicate are kept — they
  // still serve instant-nav back/forward snapshot restores (snapshots capture
  // #main-content only, so the restored page's sheets exist only here).
  function cleanupStaleSheets() {
    document.querySelectorAll('body > [data-sc-teleported]').forEach(function (el) {
      var attr = el.hasAttribute('data-ui-sheet') ? 'data-ui-sheet' : 'data-ui-sheet-overlay';
      var id = el.getAttribute(attr);
      if (!id) { el.remove(); return; }
      var dupes = document.querySelectorAll('[' + attr + '="' + cssEsc(id) + '"]');
      var hasFresh = false;
      for (var i = 0; i < dupes.length; i++) {
        if (dupes[i] !== el && !dupes[i].hasAttribute('data-sc-teleported')) { hasFresh = true; break; }
      }
      if (!hasFresh) return;
      if (el.getAttribute('aria-hidden') === 'false' && window.UISheet) {
        try { window.UISheet.close(); } catch (e) { /* no-op */ }
      }
      el.remove();
    });
  }

  /* ======================= Per-root sheet rendering ======================= */

  function chartFor(root) {
    if (root.__scChart !== undefined) return root.__scChart;
    var dataEl = root.querySelector('[data-size-chart-data]');
    var chart = null;
    if (dataEl) { try { chart = JSON.parse(dataEl.textContent); } catch (e) { chart = null; } }
    root.__scChart = (chart && chart.key) ? chart : null;
    return root.__scChart;
  }

  // In-memory init tracking (NOT a data- attribute: instant-nav snapshots
  // serialize the DOM, and a serialized "ready" flag would make restored
  // pages skip re-init and keep another product's rendered chart).
  var initedRoots = typeof WeakSet === 'function' ? new WeakSet() : null;

  function initRoot(root) {
    if (initedRoots) {
      if (initedRoots.has(root)) return;
    } else if (root.__scInited) {
      return;
    }
    var chart = chartFor(root);
    if (!chart) return;
    if (initedRoots) initedRoots.add(root);
    else root.__scInited = true;

    var sheetId = root.dataset.sheetId;
    var link = root.querySelector('[data-size-chart-link]');
    var unit = 'in';
    var rendered = false;

    /* ---- Relocate link into the Size option label row (PDP only; the set
       PDP re-places per-piece links itself — see healix-set.js). ---- */
    var sizeControl = document.querySelector(
      '.product-variants-block [data-option-name="Size" i], .product-variants-block [data-option-name="size"]'
    );
    var sizeOption = sizeControl && sizeControl.closest('.product-variants-block__option');
    var sizeLabel = sizeOption && sizeOption.querySelector('.product-variants-block__label');
    if (sizeLabel && link && !root.closest('[data-hxset]')) sizeLabel.appendChild(link);

    teleport(sheetId);
    teleport(root.dataset.warnId);

    function sheetEl() {
      return document.querySelector('[data-ui-sheet="' + sheetId + '"]');
    }

    function selectedSize() {
      var active = document.querySelector(
        '.product-variants-block__pill.active[data-option-name="Size" i], .product-variants-block__swatch.active[data-option-name="Size" i]'
      );
      return active ? active.dataset.optionValue : null;
    }

    function renderTable(sheet) {
      var host = sheet.querySelector('[data-size-chart-table]');
      if (!host) return;
      var m = chart.measurements;
      if (!m || !m.sizes || !m.sizes.length) { host.hidden = true; return; }

      var sel = selectedSize();
      var html = '<table class="size-chart__table"><thead><tr><th scope="col">Size</th>';
      (m.rows || []).forEach(function (r) { html += '<th scope="col">' + esc(r.label) + '</th>'; });
      html += '</tr></thead><tbody>';

      m.sizes.forEach(function (size, i) {
        var isSel = sel != null && String(sel).toUpperCase() === String(size).toUpperCase();
        html += '<tr data-size-row="' + esc(size) + '"' + (isSel ? ' class="is-selected"' : '') + '>';
        html += '<th scope="row">' + esc(size) + (isSel ? '<span class="size-chart__selected-badge">Your pick</span>' : '') + '</th>';
        (m.rows || []).forEach(function (r) {
          var v = fmt(r.values && r.values[i], unit);
          html += v == null ? '<td class="is-empty">&mdash;</td>' : '<td>' + esc(v) + '</td>';
        });
        html += '</tr>';
      });
      html += '</tbody></table>';
      host.innerHTML = html;

      var selRow = host.querySelector('tr.is-selected');
      if (selRow) selRow.scrollIntoView({ block: 'nearest' });
    }

    function renderInseam(sheet) {
      var host = sheet.querySelector('[data-size-chart-inseam]');
      if (!host) return;
      var ins = chart.inseam;
      if (!ins || !ins.styles || !ins.styles.length) { host.hidden = true; return; }

      // Column set = union of length keys, in a sensible order.
      var order = ['Petite', 'Regular', 'Tall'];
      var cols = [];
      ins.styles.forEach(function (s) {
        Object.keys(s.values || {}).forEach(function (k) { if (cols.indexOf(k) === -1) cols.push(k); });
      });
      cols.sort(function (a, b) {
        var ia = order.indexOf(a), ib = order.indexOf(b);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });

      var html = '<h4 class="size-chart__section-title">' + esc(ins.heading || 'Inseam') + '</h4>' +
        '<div class="size-chart__inseam-table-wrap"><table class="size-chart__inseam-table"><thead><tr><th>Style</th>';
      cols.forEach(function (c) { html += '<th>' + esc(c) + '</th>'; });
      html += '</tr></thead><tbody>';
      ins.styles.forEach(function (s) {
        html += '<tr><td>' + esc(s.label) + '</td>';
        cols.forEach(function (c) {
          var v = fmt((s.values || {})[c], unit);
          var suffix = unit === 'cm' ? ' cm' : '&Prime;';
          html += v == null ? '<td class="is-empty">&mdash;</td>' : '<td>' + esc(v) + suffix + '</td>';
        });
        html += '</tr>';
      });
      html += '</tbody></table></div>';
      host.innerHTML = html;
      host.hidden = false;
    }

    function renderStatic(sheet) {
      var note = sheet.querySelector('[data-size-chart-note]');
      if (note && chart.note) { note.textContent = chart.note; note.hidden = false; }

      var measure = sheet.querySelector('[data-size-chart-measure]');
      if (measure && ((chart.how_to_measure && chart.how_to_measure.length) || chart.measure_image)) {
        var html = '<h4 class="size-chart__section-title">How to measure</h4><div class="size-chart__measure-inner">';
        if (chart.measure_image) {
          html += '<div class="size-chart__measure-img"><img src="' + esc(chart.measure_image) + '" alt="How to measure" loading="lazy"></div>';
        }
        if (chart.how_to_measure && chart.how_to_measure.length) {
          html += '<ul class="size-chart__measure-list">';
          chart.how_to_measure.forEach(function (item) {
            html += '<li><strong>' + esc(item.label) + '</strong>' + esc(item.text) + '</li>';
          });
          html += '</ul>';
        }
        html += '</div>';
        measure.innerHTML = html;
        measure.hidden = false;
      }

      var fit = sheet.querySelector('[data-size-chart-fitnotes]');
      if (fit && chart.fit_notes && chart.fit_notes.length) {
        var fh = '<h4 class="size-chart__section-title">Fit notes</h4><ul>';
        chart.fit_notes.forEach(function (n) { fh += '<li>' + esc(n) + '</li>'; });
        fh += '</ul>';
        fit.innerHTML = fh;
        fit.hidden = false;
      }
    }

    function updateHint(sheet) {
      var hint = sheet.querySelector('[data-size-chart-hint]');
      if (hint) hint.textContent = unit === 'cm' ? 'Garment measurements in centimetres' : 'Garment measurements in inches';
      sheet.querySelectorAll('[data-size-chart-unit]').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.sizeChartUnit === unit);
      });
    }

    function renderAll() {
      var sheet = sheetEl();
      if (!sheet) return;
      renderTable(sheet);
      renderInseam(sheet);
      updateHint(sheet);
      if (!rendered) { renderStatic(sheet); rendered = true; }
    }

    var sheet = sheetEl();
    if (sheet) {
      sheet.addEventListener('click', function (e) {
        // Sheets outlive PJAX swaps (they're teleported to <body> and reused
        // across pages when ids match); only the CURRENT page's root may
        // respond, or a stale closure would render the previous chart.
        if (!root.isConnected) return;
        var unitBtn = e.target.closest('[data-size-chart-unit]');
        if (unitBtn) {
          unit = unitBtn.dataset.sizeChartUnit;
          renderAll();
          return;
        }
        var row = e.target.closest('tr[data-size-row]');
        if (row) {
          var was = row.classList.contains('is-tapped');
          sheet.querySelectorAll('tr.is-tapped').forEach(function (r) { r.classList.remove('is-tapped'); });
          if (!was) row.classList.add('is-tapped');
        }
      });
    }

    // Render on open. Marking "viewed" is handled by ONE global listener
    // (below) that resolves the family from the live DOM — never from here.
    document.addEventListener('ui-sheet:open', function (e) {
      if (!e.detail || e.detail.id !== sheetId) return;
      if (!root.isConnected) return; // stale listener from a previous PJAX page
      renderAll();
    });
  }

  /* ============================ Add-to-cart gate =========================== */

  var bypass = false;
  var pending = null; // { onProceed, viewSheetId, dismissKey }

  // The active normal-PDP root (warning enabled). Set-PDP piece roots render
  // with data-warning-enabled="false" and are gated via gateSet() instead.
  function pdpRoot() {
    var root = document.querySelector('[data-size-chart-root][data-warning-enabled="true"]');
    return (root && root.isConnected && chartFor(root)) ? root : null;
  }

  function variantStillPending(scope) {
    // product.js pending gate owns the flow while a deferred size/length
    // option is unselected (it marks the buy buttons with .is-pending).
    var btn = (scope || document).querySelector('.product-add-to-cart-block__btn.is-pending, [data-floating-btn].is-pending');
    return !!btn;
  }

  // Decision logic (deterministic): given candidate chart roots, warn when
  //   - the product wasn't already dismissed ("Add anyway") this session, and
  //   - some root's FAMILY chart hasn't been viewed this session.
  // (A root only exists when the product resolves a size chart, so "has a
  // size dimension" is implied by the root's presence.)
  // Returns true when the prompt was opened (caller must stop the add).
  function gate(opts) {
    if (!window.UISheet) return false; // never block the add without a prompt to show
    if (!opts.warnId || !document.querySelector('[data-ui-sheet="' + cssEsc(opts.warnId) + '"]')) return false;
    if (!opts.roots || !opts.roots.length) return false;
    if (isDismissed(opts.dismissKey)) return false;
    var target = null;
    for (var i = 0; i < opts.roots.length; i++) {
      var r = opts.roots[i];
      if (!r || !r.isConnected || !chartFor(r)) continue;
      var family = r.dataset.family || r.dataset.productHandle;
      if (family && !isViewed(family)) { target = r; break; }
    }
    if (!target) return false;
    pending = {
      onProceed: opts.onProceed,
      viewSheetId: target.dataset.sheetId,
      dismissKey: opts.dismissKey
    };
    window.UISheet.open(opts.warnId);
    return true;
  }

  // Set-PDP hook, called by healix-set.js from the "Add set to bag" flow once
  // both variants resolve. Checks BOTH pieces' families; "View size chart"
  // opens the first unviewed piece's chart; "Add anyway" dismisses the SET
  // shell product for the session and resumes the add.
  function gateSet(setRoot, onProceed) {
    if (!setRoot || bypass) return false;
    var warnId = setRoot.getAttribute('data-hxset-warn-id');
    if (!warnId) return false; // warning disabled in theme settings
    var roots = Array.prototype.slice.call(setRoot.querySelectorAll('[data-size-chart-root]'));
    return gate({
      roots: roots,
      warnId: warnId,
      dismissKey: setRoot.getAttribute('data-set-handle') || '',
      onProceed: onProceed
    });
  }

  function bindGlobalListeners() {
    // Mark "viewed" whenever ANY chart sheet opens, from ANY trigger (PDP
    // link, set per-piece links, the warning's View button). Resolved from the
    // live DOM so PJAX navigations can't poison it with stale closures.
    document.addEventListener('ui-sheet:open', function (e) {
      var id = e.detail && e.detail.id;
      if (!id) return;
      var roots = document.querySelectorAll('[data-size-chart-root]');
      for (var i = 0; i < roots.length; i++) {
        var r = roots[i];
        if (!r.isConnected || r.dataset.sheetId !== id) continue;
        markViewed(r.dataset.family || r.dataset.productHandle);
        return;
      }
    });

    // Main PDP add-to-cart form (capture; runs after product.js' pending gate).
    document.addEventListener('submit', function (e) {
      if (bypass || e.defaultPrevented) return;
      var form = e.target.closest && e.target.closest('.product-add-to-cart-block__form');
      if (!form) return;
      if (form.closest('[data-ui-sheet], [data-qv-root]')) return; // quick-view / other sheets
      if (variantStillPending(form.closest('.product-add-to-cart-block') || document)) return;
      var root = pdpRoot();
      if (!root) return;
      var opened = gate({
        roots: [root],
        warnId: root.dataset.warnId,
        dismissKey: root.dataset.productHandle,
        onProceed: function () {
          bypass = true;
          try {
            if (form.requestSubmit) form.requestSubmit();
            else form.submit();
          } finally {
            setTimeout(function () { bypass = false; }, 0);
          }
        }
      });
      if (opened) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);

    // Floating bar add-to-cart button.
    document.addEventListener('click', function (e) {
      if (bypass || e.defaultPrevented) return;
      if (!e.target.closest) return;
      var btn = e.target.closest('[data-floating-btn="add_to_cart"]');
      if (!btn || btn.disabled) return;
      if (btn.classList.contains('is-pending') || variantStillPending()) return;
      var root = pdpRoot();
      if (!root) return;
      var opened = gate({
        roots: [root],
        warnId: root.dataset.warnId,
        dismissKey: root.dataset.productHandle,
        onProceed: function () {
          bypass = true;
          try { btn.click(); } finally { setTimeout(function () { bypass = false; }, 0); }
        }
      });
      if (opened) { e.preventDefault(); e.stopImmediatePropagation(); }
    }, true);

    // Warning prompt actions (delegated; works for the PDP and the set sheet).
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      if (e.target.closest('[data-size-chart-warn-view]')) {
        var p = pending;
        pending = null;
        // Opening the chart marks the family viewed (ui-sheet:open listener);
        // the shopper re-triggers the add themselves after reading.
        if (p && window.UISheet) window.UISheet.open(p.viewSheetId); // auto-closes the warning
        return;
      }
      if (e.target.closest('[data-size-chart-warn-add]')) {
        var action = pending;
        pending = null;
        if (window.UISheet) window.UISheet.close();
        if (!action) return;
        markDismissed(action.dismissKey);
        if (action.onProceed) action.onProceed();
      }
    });
  }

  /* ================================= Boot ================================= */

  function boot() {
    cleanupStaleSheets();
    document.querySelectorAll('[data-size-chart-root]').forEach(initRoot);
    // The set PDP's warning sheet isn't owned by any chart root — teleport it
    // to <body> here so it can't be trapped by a transformed ancestor.
    document.querySelectorAll('[data-hxset]').forEach(function (r) {
      teleport(r.getAttribute('data-hxset-warn-id'));
    });
  }

  bindGlobalListeners();
  window.__healixSizeChart = { boot: boot };
  window.HealixSizeChart = {
    gateSet: gateSet,
    markViewed: markViewed,
    isViewed: isViewed
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor re-renders / PJAX section loads: re-boot (idempotent per root).
  document.addEventListener('shopify:section:load', function () { boot(); });
})();
